import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, FileText, Search, Calendar, Loader2, Edit, Save, X, Briefcase, Printer } from 'lucide-react';
import { getServiceCalls, getDailyLogs, getDealClosures, getReportPrefs, toggleFavoriteReport, addRecentReport } from '../api';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { ReportPicker } from '../components/ReportPicker';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../components/ui/resizable';
import CustomerReport from './CustomerReport';
import ServiceCallsReport from './ServiceCallsReport';
import DailyLogsList from './DailyLogsList';
import DealClosuresList from './DealClosuresList';
import DebtorsReport from './DebtorsReport';
import MonthlyReport from './MonthlyReport';
import ChecksReport from './ChecksReport';
import ProfitByCustomer from './ProfitByCustomer';
import ConsolidatedInvoicesList from './ConsolidatedInvoicesList';
import VatReport from './VatReport';
import DailyIncomeReport from './DailyIncomeReport';
import ConsolidatedInvoice from './ConsolidatedInvoice';
import { PaneNavContext } from '../lib/paneNav';

const REPORT_COMPONENTS = {
  '/customer-report': CustomerReport,
  '/service-calls-report': ServiceCallsReport,
  '/daily-logs': DailyLogsList,
  '/deal-closures': DealClosuresList,
  '/debtors-report': DebtorsReport,
  '/monthly-report': MonthlyReport,
  '/checks-report': ChecksReport,
  '/profit-by-customer': ProfitByCustomer,
  '/consolidated-invoices': ConsolidatedInvoicesList,
  '/vat-report': VatReport,
  '/daily-income': DailyIncomeReport,
};

const COST_RE = /עלות|רווח/;
// Customer print: temporarily hide table columns / stat cards that show cost or profit
const printForCustomer = (root) => {
  const hidden = [];
  const hide = (el) => { if (el && el.style.display !== 'none') { hidden.push([el, el.style.display]); el.style.display = 'none'; } };
  root.querySelectorAll('table').forEach((table) => {
    const head = table.querySelector('thead tr');
    if (!head) return;
    Array.from(head.children).forEach((th, idx) => {
      if (!COST_RE.test(th.textContent)) return;
      table.querySelectorAll('tr').forEach((tr) => { if (tr.children.length === head.children.length) hide(tr.children[idx]); });
    });
  });
  root.querySelectorAll('p, span, div').forEach((el) => {
    if (el.children.length === 0 && COST_RE.test(el.textContent) && el.textContent.length < 40) hide(el.parentElement);
  });
  const restore = () => { hidden.forEach(([el, d]) => { el.style.display = d; }); window.removeEventListener('afterprint', restore); };
  window.addEventListener('afterprint', restore);
  window.print();
};

const EMBED_CSS = `
  @media print { [data-testid="report-picker-card"], [data-testid="floating-nav-group"], .print-hide-in-report { display: none !important; } }
  .embedded-report > div:first-child, .compare-pane-body > div:first-child { min-height: auto; background: transparent; padding: 0; }
  .embedded-report .sticky { top: var(--picker-h) !important; z-index: 30; }
  .compare-pane-scroll .sticky { top: 0 !important; z-index: 30; }
  .compare-stack .sticky { top: var(--picker-h) !important; z-index: 30; }
  .compare-pane-body .container { max-width: none; }
`;

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
};

// Keeps consolidated-invoice navigation inside a split-view pane instead of leaving the reports page
const PaneHost = ({ children }) => {
  const [invoiceId, setInvoiceId] = useState(null);
  const handler = (to) => {
    const m = to.match(/^\/consolidated-invoice\?id=([^&]+)/);
    if (m) { setInvoiceId(decodeURIComponent(m[1])); return true; }
    if (to === '/consolidated-invoices' && invoiceId) { setInvoiceId(null); return true; }
    return false;
  };
  return (
    <PaneNavContext.Provider value={handler}>
      {invoiceId ? <ConsolidatedInvoice key={invoiceId} invoiceId={invoiceId} /> : children}
    </PaneNavContext.Provider>
  );
};

const ComparePane = ({ title, onClose, testId, accent, children, scroll }) => (
  <div className={`flex flex-col h-full bg-white rounded-xl shadow-lg border-t-4 ${accent}`} data-testid={testId}>
    <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50 rounded-t-xl shrink-0">
      <span className="font-bold text-gray-800 truncate" data-testid={`${testId}-title`}>{title}</span>
      <button type="button" onClick={onClose} className="p-1.5 rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors" title="סגור דוח זה" data-testid={`${testId}-close`}>
        <X className="w-5 h-5" />
      </button>
    </div>
    <div className={`compare-pane-body p-3 ${scroll ? 'compare-pane-scroll flex-1 overflow-y-auto' : ''}`} data-testid={`${testId}-body`}><PaneHost>{children}</PaneHost></div>
  </div>
);

const CompareView = ({ isDesktop, height, pickerOffset, panes }) => {
  if (!isDesktop) {
    return <div className="compare-stack space-y-6" style={{ '--picker-h': pickerOffset }} data-testid="compare-view">{panes.map((p) => <ComparePane key={p.testId} {...p} />)}</div>;
  }
  return (
    <div style={{ height }} data-testid="compare-view">
      <ResizablePanelGroup direction="horizontal" dir="rtl" className="gap-0">
        <ResizablePanel defaultSize={50} minSize={20}><ComparePane {...panes[0]} scroll /></ResizablePanel>
        <ResizableHandle withHandle className="mx-2 w-1.5 rounded-full bg-indigo-200 hover:bg-indigo-400 transition-colors" data-testid="compare-resize-handle" />
        <ResizablePanel defaultSize={50} minSize={20}><ComparePane {...panes[1]} scroll /></ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const navigate = useNavigate();
  const [searchName, setSearchName] = useState('');
  const [searchDateFrom, setSearchDateFrom] = useState('');
  const [searchDateTo, setSearchDateTo] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editData, setEditData] = useState({});
  const [allData, setAllData] = useState({
    serviceCalls: [],
    dailyLogs: [],
    dealClosures: []
  });
  
  // Work Report State
  const [showWorkReport, setShowWorkReport] = useState(false);
  const [workList, setWorkList] = useState([]);
  const [selectedWorkId, setSelectedWorkId] = useState('');
  const [selectedWork, setSelectedWork] = useState(null);
  const [workDetails, setWorkDetails] = useState([]);
  const [loadingWorkReport, setLoadingWorkReport] = useState(false);
  const workReportRef = useRef(null);

  // Scroll to work report when opened
  const handleOpenWorkReport = () => {
    setShowWorkReport(true);
    setTimeout(() => {
      workReportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  useEffect(() => {
    loadAllData();
    loadWorkList();
  }, []);

  const loadWorkList = async () => {
    try {
      const response = await fetch(`${API}/work`);
      if (response.ok) {
        const data = await response.json();
        setWorkList(data);
      }
    } catch (error) {
      console.error('Error loading work list:', error);
    }
  };

  const loadWorkReport = async (workId) => {
    if (!workId) {
      setSelectedWork(null);
      setWorkDetails([]);
      return;
    }
    
    setLoadingWorkReport(true);
    try {
      // Find work details
      const work = workList.find(w => w.id === workId);
      setSelectedWork(work);
      
      // Load work detail items
      const response = await fetch(`${API}/work-details/${workId}`);
      if (response.ok) {
        const details = await response.json();
        setWorkDetails(details);
      }
    } catch (error) {
      console.error('Error loading work report:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון פרטי עבודה',
        variant: 'destructive'
      });
    } finally {
      setLoadingWorkReport(false);
    }
  };

  const handleWorkSelect = (workId) => {
    setSelectedWorkId(workId);
    loadWorkReport(workId);
  };

  const printWorkReport = () => {
    if (!selectedWorkId) {
      toast({
        title: 'שגיאה',
        description: 'אנא בחר עבודה',
        variant: 'destructive'
      });
      return;
    }
    // Navigate to print page
    navigate(`/print-work-report?workId=${selectedWorkId}`);
  };

  const loadAllData = async () => {
    try {
      const [serviceCalls, dailyLogs, dealClosures] = await Promise.all([
        getServiceCalls(),
        getDailyLogs(),
        getDealClosures()
      ]);
      
      setAllData({
        serviceCalls,
        dailyLogs,
        dealClosures
      });
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSearch = () => {
    if (!searchName && !searchDateFrom && !searchDateTo) {
      toast({
        title: 'שגיאה',
        description: 'נא למלא לפחות שדה חיפוש אחד',
        variant: 'destructive'
      });
      return;
    }

    setIsSearching(true);
    
    const results = {
      serviceCalls: [],
      dailyLogs: [],
      dealClosures: []
    };

    // Filter Service Calls
    results.serviceCalls = allData.serviceCalls.filter(item => {
      let matchName = true;
      let matchDate = true;

      if (searchName) {
        matchName = item.customerName?.toLowerCase().includes(searchName.toLowerCase()) ||
                   item.branch?.toLowerCase().includes(searchName.toLowerCase());
      }

      if (searchDateFrom || searchDateTo) {
        const itemDate = new Date(item.date);
        if (searchDateFrom) {
          matchDate = matchDate && itemDate >= new Date(searchDateFrom);
        }
        if (searchDateTo) {
          matchDate = matchDate && itemDate <= new Date(searchDateTo);
        }
      }

      return matchName && matchDate;
    });

    // Filter Daily Logs
    results.dailyLogs = allData.dailyLogs.filter(item => {
      let matchName = true;
      let matchDate = true;

      if (searchName) {
        matchName = item.customerName?.toLowerCase().includes(searchName.toLowerCase());
      }

      if (searchDateFrom || searchDateTo) {
        const itemDate = new Date(item.date);
        if (searchDateFrom) {
          matchDate = matchDate && itemDate >= new Date(searchDateFrom);
        }
        if (searchDateTo) {
          matchDate = matchDate && itemDate <= new Date(searchDateTo);
        }
      }

      return matchName && matchDate;
    });

    // Filter Deal Closures
    results.dealClosures = allData.dealClosures.filter(item => {
      let matchName = true;
      let matchDate = true;

      if (searchName) {
        matchName = item.customerName?.toLowerCase().includes(searchName.toLowerCase());
      }

      if (searchDateFrom || searchDateTo) {
        const itemDate = new Date(item.date);
        if (searchDateFrom) {
          matchDate = matchDate && itemDate >= new Date(searchDateFrom);
        }
        if (searchDateTo) {
          matchDate = matchDate && itemDate <= new Date(searchDateTo);
        }
      }

      return matchName && matchDate;
    });

    setSearchResults(results);
    setIsSearching(false);

    const totalResults = results.serviceCalls.length + results.dailyLogs.length + results.dealClosures.length;
    
    toast({
      title: 'חיפוש הושלם',
      description: `נמצאו ${totalResults} תוצאות`
    });
  };

  const clearSearch = () => {
    setSearchName('');
    setSearchDateFrom('');
    setSearchDateTo('');
    setSearchResults(null);
  };

  const startEdit = (item, type) => {
    setEditingItem({ id: item.id, type });
    setEditData({ ...item });
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setEditData({});
  };

  const saveEdit = async () => {
    if (!editingItem) return;

    try {
      const { id, type } = editingItem;
      let endpoint = '';
      
      if (type === 'serviceCall') {
        endpoint = `${API}/service-calls/${id}`;
      } else if (type === 'dailyLog') {
        endpoint = `${API}/daily-logs/${id}`;
      } else if (type === 'dealClosure') {
        endpoint = `${API}/deal-closures/${id}`;
      }

      await axios.put(endpoint, editData);

      // Update local data
      await loadAllData();
      
      // Re-run search to show updated results
      handleSearch();

      toast({
        title: 'הצלחה',
        description: 'הנתונים עודכנו בהצלחה'
      });

      cancelEdit();
    } catch (error) {
      console.error('Error saving:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לשמור את השינויים',
        variant: 'destructive'
      });
    }
  };

  const handleEditChange = (field, value) => {
    setEditData({
      ...editData,
      [field]: value
    });
  };

  const reportCards = [
    {
      title: 'דווח ללקוח',
      description: 'דיווח עסקאות ללקוח',
      path: '/customer-report',
      color: 'from-purple-600 to-pink-600',
      hoverColor: 'hover:from-purple-700 hover:to-pink-700'
    },
    {
      title: 'דווח מכללה טכנולוגית',
      description: 'דיווח קריאות מכללה',
      path: '/service-calls-report',
      color: 'from-blue-600 to-cyan-600',
      hoverColor: 'hover:from-blue-700 hover:to-cyan-700'
    },
    {
      title: 'דווח רישום יומי',
      description: 'דיווח יומי מפורט',
      path: '/daily-logs',
      color: 'from-indigo-600 to-blue-600',
      hoverColor: 'hover:from-indigo-700 hover:to-blue-700'
    },
    {
      title: 'דווח סגירות עסקה',
      description: 'דיווח עסקאות סגורות',
      path: '/deal-closures',
      color: 'from-green-600 to-emerald-600',
      hoverColor: 'hover:from-green-700 hover:to-emerald-700'
    },
    {
      title: 'דווח חייבים',
      description: 'רשימת לקוחות חייבים',
      path: '/debtors-report',
      color: 'from-red-600 to-orange-600',
      hoverColor: 'hover:from-red-700 hover:to-orange-700'
    },
    {
      title: 'דווח חודשי',
      description: 'סיכום רווח חודשי וקניות לפי ספק',
      path: '/monthly-report',
      color: 'from-indigo-600 to-purple-600',
      hoverColor: 'hover:from-indigo-700 hover:to-purple-700'
    },
    {
      title: 'דוח שיקים',
      description: 'ריכוז שיקים לפי תאריך פירעון',
      path: '/checks-report',
      color: 'from-amber-600 to-yellow-600',
      hoverColor: 'hover:from-amber-700 hover:to-yellow-700'
    },
    {
      title: 'דוח רווח לפי לקוח',
      description: 'רווח מצטבר לכל לקוח על פני הזמן',
      path: '/profit-by-customer',
      color: 'from-emerald-600 to-green-600',
      hoverColor: 'hover:from-emerald-700 hover:to-green-700'
    },
    {
      title: 'חשבונות מרוכזים',
      description: 'חשבונות מרוכזים ללקוחות עם מצב תשלום',
      path: '/consolidated-invoices',
      color: 'from-teal-600 to-cyan-600',
      hoverColor: 'hover:from-teal-700 hover:to-cyan-700'
    },
    {
      title: 'דוח מע"מ',
      description: 'מע"מ עסקאות ותשומות לפי חודש',
      path: '/vat-report',
      color: 'from-indigo-600 to-blue-600',
      hoverColor: 'hover:from-indigo-700 hover:to-blue-700'
    },
    {
      title: 'הכנסות לפי יום',
      description: 'כסף שנכנס, עבודות שבוצעו ורווח בכל יום',
      path: '/daily-income',
      color: 'from-emerald-600 to-green-600',
      hoverColor: 'hover:from-emerald-700 hover:to-green-700'
    }
  ];

  const allReports = [...reportCards, { title: 'דוח עבודה', description: 'דוח פירוט עבודה ללקוח', path: '__work__' }];
  const [reportPrefs, setReportPrefs] = useState({ favorites: [], recent: [] });
  const [searchParams, setSearchParams] = useSearchParams();
  const pickerRef = useRef(null);
  const [pickerHeight, setPickerHeight] = useState(0);

  useEffect(() => {
    if (!pickerRef.current) return;
    const ro = new ResizeObserver(([entry]) => setPickerHeight(entry.target.offsetHeight));
    ro.observe(pickerRef.current);
    return () => ro.disconnect();
  }, []);
  const activePath = searchParams.get('report');
  const ActiveReport = REPORT_COMPONENTS[activePath];
  const secondPath = searchParams.get('report2');
  const SecondReport = ActiveReport && secondPath !== activePath ? REPORT_COMPONENTS[secondPath] : null;
  const secondTitle = SecondReport ? reportCards.find(r => r.path === secondPath)?.title : null;
  const isDesktop = useIsDesktop();
  const activeTitle = ActiveReport ? reportCards.find(r => r.path === activePath)?.title : (showWorkReport ? 'דוח עבודה' : null);

  useEffect(() => {
    getReportPrefs().then(setReportPrefs).catch(() => {});
  }, []);

  const handleToggleFavorite = (path) => {
    setReportPrefs(prev => ({
      ...prev,
      favorites: prev.favorites.includes(path) ? prev.favorites.filter(p => p !== path) : [...prev.favorites, path]
    }));
    toggleFavoriteReport(path).then(setReportPrefs).catch(() => {
      toast({ title: 'שגיאה', description: 'לא ניתן לשמור את המועדפים', variant: 'destructive' });
      getReportPrefs().then(setReportPrefs).catch(() => {});
    });
  };

  const openReport = (path) => {
    addRecentReport(path).catch(() => {});
    setReportPrefs(prev => ({ ...prev, recent: [path, ...prev.recent.filter(p => p !== path)].slice(0, 3) }));
    if (path === '__work__') {
      if (activePath) setSearchParams({});
      if (showWorkReport) workReportRef.current?.scrollIntoView({ behavior: 'smooth' });
      else handleOpenWorkReport();
    } else {
      setShowWorkReport(false);
      setSearchParams(SecondReport && secondPath !== path ? { report: path, report2: secondPath } : { report: path });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const openSecond = (path) => {
    addRecentReport(path).catch(() => {});
    setReportPrefs(prev => ({ ...prev, recent: [path, ...prev.recent.filter(p => p !== path)].slice(0, 3) }));
    setSearchParams({ report: activePath, report2: path });
  };

  const closeSecond = () => setSearchParams(activePath ? { report: activePath } : {});

  const closeReport = () => {
    setShowWorkReport(false);
    setSearchParams(SecondReport ? { report: secondPath } : {});
  };

  const navigateToReport = (path) => {
    // Pass search params if they exist
    const params = new URLSearchParams();
    if (searchName) params.append('name', searchName);
    if (searchDateFrom) params.append('from', searchDateFrom);
    if (searchDateTo) params.append('to', searchDateTo);
    
    const queryString = params.toString();
    navigate(queryString ? `${path}?${queryString}` : path);
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 ${SecondReport ? 'lg:pl-20' : ''}`} dir="rtl">
      <div className={`container mx-auto ${SecondReport ? 'max-w-none' : 'max-w-6xl'}`}>
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">דיווחים</h1>
            <p className="text-gray-600">בחר את סוג הדיווח המבוקש</p>
          </div>
        </div>

        {/* Report Picker */}
        <Card ref={pickerRef} className="mb-8 shadow-xl border-0 sticky top-0 z-40 bg-white/95 backdrop-blur-md" data-testid="report-picker-card">
          <CardContent className="p-4 md:p-5">
            <ReportPicker
              activeTitle={activeTitle}
              onClose={closeReport}
              reports={allReports}
              favorites={reportPrefs.favorites}
              recent={reportPrefs.recent}
              onOpen={openReport}
              onToggleFavorite={handleToggleFavorite}
              compareReports={ActiveReport ? reportCards.filter(r => r.path !== activePath) : null}
              secondTitle={secondTitle}
              onOpenSecond={openSecond}
              onCloseSecond={closeSecond}
            />
          </CardContent>
        </Card>

        <style>{EMBED_CSS}</style>
        {SecondReport ? (
          <CompareView
            isDesktop={isDesktop}
            height={`calc(100vh - ${pickerHeight + 24}px)`}
            pickerOffset={`${pickerHeight + 12}px`}
            panes={[
              { title: activeTitle, onClose: closeReport, testId: 'compare-pane-1', accent: 'border-indigo-500', children: <ActiveReport key={activePath} /> },
              { title: secondTitle, onClose: closeSecond, testId: 'compare-pane-2', accent: 'border-amber-500', children: <SecondReport key={secondPath} /> },
            ]}
          />
        ) : ActiveReport ? (
          <div className="embedded-report" style={{ '--picker-h': `${pickerHeight + 12}px` }} data-testid="embedded-report">
            <div className="flex justify-end gap-2 mb-3 print:hidden">
              <Button onClick={() => window.print()} variant="outline" className="border-2 bg-white" data-testid="report-print-pdf-btn">
                <Printer className="w-4 h-4 ml-2" />הדפס / שמור PDF
              </Button>
              <Button onClick={() => printForCustomer(document.querySelector('[data-testid="embedded-report"]'))} variant="outline" className="border-2 border-blue-300 text-blue-700 bg-white hover:bg-blue-50" data-testid="report-print-customer-btn">
                <FileText className="w-4 h-4 ml-2" />הדפס ללקוח (בלי עלות ורווח)
              </Button>
            </div>
            <ActiveReport key={activePath} />
          </div>
        ) : (<>
        {/* Search Section */}
        <Card className="mb-8 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <CardTitle className="text-2xl flex items-center gap-2">
              <Search className="w-6 h-6" />
              חיפוש בדיווחים
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-4">
              {/* Search by Name */}
              <div className="space-y-2">
                <Label htmlFor="searchName" className="text-base font-semibold">
                  חיפוש לפי שם
                </Label>
                <Input
                  id="searchName"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="הזן שם לקוח / איש קשר"
                  className="text-base py-5 border-2"
                />
              </div>

              {/* Date From */}
              <div className="space-y-2">
                <Label htmlFor="dateFrom" className="text-base font-semibold">
                  מתאריך
                </Label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={searchDateFrom}
                  onChange={(e) => setSearchDateFrom(e.target.value)}
                  className="text-base py-5 border-2"
                />
              </div>

              {/* Date To */}
              <div className="space-y-2">
                <Label htmlFor="dateTo" className="text-base font-semibold">
                  עד תאריך
                </Label>
                <Input
                  id="dateTo"
                  type="date"
                  value={searchDateTo}
                  onChange={(e) => setSearchDateTo(e.target.value)}
                  className="text-base py-5 border-2"
                />
              </div>
            </div>

            {/* Search and Clear Buttons */}
            <div className="mt-6 flex gap-3 justify-end">
              {(searchName || searchDateFrom || searchDateTo) && (
                <Button
                  onClick={clearSearch}
                  variant="outline"
                  className="border-2"
                >
                  נקה חיפוש
                </Button>
              )}
              <Button
                onClick={handleSearch}
                disabled={isSearching}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8"
              >
                {isSearching ? (
                  <>
                    <Loader2 className="ml-2 w-5 h-5 animate-spin" />
                    מחפש...
                  </>
                ) : (
                  <>
                    <Search className="ml-2 w-5 h-5" />
                    חיפוש
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Search Results */}
        {searchResults && (
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">תוצאות חיפוש - טבלה ניתנת לעריכה</h2>
            
            {/* Service Calls Table */}
            {searchResults.serviceCalls.length > 0 && (
              <Card className="mb-6 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
                  <CardTitle className="text-2xl">מכללה טכנולוגית ({searchResults.serviceCalls.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-blue-100">
                          <th className="border p-3 text-right font-bold">איש קשר</th>
                          <th className="border p-3 text-right font-bold">סניף</th>
                          <th className="border p-3 text-center font-bold">תאריך קריאה</th>
                          <th className="border p-3 text-right font-bold">תיאור תקלה</th>
                          <th className="border p-3 text-center font-bold">תאריך יצירה</th>
                          <th className="border p-3 text-center font-bold w-32">פעולות</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.serviceCalls.map((call) => {
                          const isEditing = editingItem?.id === call.id && editingItem?.type === 'serviceCall';
                          return (
                            <tr key={call.id} className="hover:bg-blue-50">
                              <td className="border p-3">
                                {isEditing ? (
                                  <Input
                                    value={editData.customerName || ''}
                                    onChange={(e) => handleEditChange('customerName', e.target.value)}
                                    className="w-full"
                                  />
                                ) : (
                                  call.customerName
                                )}
                              </td>
                              <td className="border p-3">
                                {isEditing ? (
                                  <Input
                                    value={editData.branch || ''}
                                    onChange={(e) => handleEditChange('branch', e.target.value)}
                                    className="w-full"
                                  />
                                ) : (
                                  call.branch
                                )}
                              </td>
                              <td className="border p-3 text-center">
                                {isEditing ? (
                                  <Input
                                    type="date"
                                    value={editData.callDate || ''}
                                    onChange={(e) => handleEditChange('callDate', e.target.value)}
                                    className="w-full"
                                  />
                                ) : (
                                  call.callDate || '-'
                                )}
                              </td>
                              <td className="border p-3">
                                {isEditing ? (
                                  <Textarea
                                    value={editData.issueDescription || ''}
                                    onChange={(e) => handleEditChange('issueDescription', e.target.value)}
                                    className="w-full min-h-[80px]"
                                  />
                                ) : (
                                  <div className="max-w-md">{call.issueDescription}</div>
                                )}
                              </td>
                              <td className="border p-3 text-center text-sm">{formatDate(call.date)}</td>
                              <td className="border p-3 text-center">
                                {isEditing ? (
                                  <div className="flex gap-2 justify-center">
                                    <Button
                                      size="sm"
                                      onClick={saveEdit}
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      <Save className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={cancelEdit}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => startEdit(call, 'serviceCall')}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Daily Logs Table */}
            {searchResults.dailyLogs.length > 0 && (
              <Card className="mb-6 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                  <CardTitle className="text-2xl">רישום יומי ({searchResults.dailyLogs.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-indigo-100">
                          <th className="border p-3 text-right font-bold">שם לקוח</th>
                          <th className="border p-3 text-right font-bold">שם ספק</th>
                          <th className="border p-3 text-right font-bold">תשלום</th>
                          <th className="border p-3 text-center font-bold">תאריך</th>
                          <th className="border p-3 text-center font-bold w-32">פעולות</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.dailyLogs.map((log) => {
                          const isEditing = editingItem?.id === log.id && editingItem?.type === 'dailyLog';
                          return (
                            <tr key={log.id} className="hover:bg-indigo-50">
                              <td className="border p-3">
                                {isEditing ? (
                                  <Input
                                    value={editData.customerName || ''}
                                    onChange={(e) => handleEditChange('customerName', e.target.value)}
                                    className="w-full"
                                  />
                                ) : (
                                  log.customerName
                                )}
                              </td>
                              <td className="border p-3">
                                {isEditing ? (
                                  <Input
                                    value={editData.supplierName || ''}
                                    onChange={(e) => handleEditChange('supplierName', e.target.value)}
                                    className="w-full"
                                  />
                                ) : (
                                  log.supplierName || '-'
                                )}
                              </td>
                              <td className="border p-3">
                                {isEditing ? (
                                  <Input
                                    value={editData.payment || ''}
                                    onChange={(e) => handleEditChange('payment', e.target.value)}
                                    className="w-full"
                                  />
                                ) : (
                                  log.payment || '-'
                                )}
                              </td>
                              <td className="border p-3 text-center text-sm">{formatDate(log.date)}</td>
                              <td className="border p-3 text-center">
                                {isEditing ? (
                                  <div className="flex gap-2 justify-center">
                                    <Button
                                      size="sm"
                                      onClick={saveEdit}
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      <Save className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={cancelEdit}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => startEdit(log, 'dailyLog')}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Deal Closures Table */}
            {searchResults.dealClosures.length > 0 && (
              <Card className="mb-6 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
                  <CardTitle className="text-2xl">סגירות עסקה ({searchResults.dealClosures.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-green-100">
                          <th className="border p-3 text-right font-bold">שם לקוח</th>
                          <th className="border p-3 text-center font-bold">תאריך</th>
                          <th className="border p-3 text-center font-bold w-32">פעולות</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.dealClosures.map((deal) => {
                          const isEditing = editingItem?.id === deal.id && editingItem?.type === 'dealClosure';
                          return (
                            <tr key={deal.id} className="hover:bg-green-50">
                              <td className="border p-3">
                                {isEditing ? (
                                  <Input
                                    value={editData.customerName || ''}
                                    onChange={(e) => handleEditChange('customerName', e.target.value)}
                                    className="w-full"
                                  />
                                ) : (
                                  deal.customerName
                                )}
                              </td>
                              <td className="border p-3 text-center text-sm">{formatDate(deal.date)}</td>
                              <td className="border p-3 text-center">
                                {isEditing ? (
                                  <div className="flex gap-2 justify-center">
                                    <Button
                                      size="sm"
                                      onClick={saveEdit}
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      <Save className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={cancelEdit}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => startEdit(deal, 'dealClosure')}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No Results */}
            {searchResults.serviceCalls.length === 0 && 
             searchResults.dailyLogs.length === 0 && 
             searchResults.dealClosures.length === 0 && (
              <Card className="shadow-lg">
                <CardContent className="py-12 text-center">
                  <p className="text-xl text-gray-500">לא נמצאו תוצאות לחיפוש זה</p>
                  <Button
                    onClick={clearSearch}
                    variant="outline"
                    className="mt-4"
                  >
                    נקה חיפוש
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Work Report Section */}
        {showWorkReport && (
          <Card ref={workReportRef} className="mt-8 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white print-hide">
              <CardTitle className="text-2xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-6 h-6" />
                  דוח עבודה ללקוח
                </div>
                <Button
                  onClick={printWorkReport}
                  variant="outline"
                  className="bg-white text-orange-600 hover:bg-orange-50"
                >
                  <Printer className="ml-2 w-5 h-5" />
                  הדפסה
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {/* Work Selection */}
              <div className="mb-6 print-hide">
                <Label className="text-lg font-semibold mb-2 block">בחר עבודה:</Label>
                <select
                  value={selectedWorkId}
                  onChange={(e) => handleWorkSelect(e.target.value)}
                  className="w-full md:w-1/2 px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-lg"
                >
                  <option value="">-- בחר עבודה --</option>
                  {workList.map(work => (
                    <option key={work.id} value={work.id}>
                      {work.workName} - {work.clientName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Loading */}
              {loadingWorkReport && (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-orange-500" />
                  <p className="mt-2 text-gray-600">טוען נתונים...</p>
                </div>
              )}

              {/* Work Report Content */}
              {selectedWork && !loadingWorkReport && (
                <div className="print-work-report space-y-6">
                  {/* Print Header */}
                  <div className="hidden print:block text-center mb-6">
                    <h1 className="text-3xl font-bold">דוח עבודה ללקוח</h1>
                  </div>
                  
                  {/* Client Details */}
                  <div className="bg-orange-50 p-6 rounded-lg border-2 border-orange-200">
                    <h3 className="text-xl font-bold text-orange-800 mb-4">פרטי לקוח</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <span className="font-semibold text-gray-600">שם העבודה:</span>
                        <span className="mr-2 text-lg">{selectedWork.workName}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-600">שם מזמין:</span>
                        <span className="mr-2 text-lg">{selectedWork.clientName}</span>
                      </div>
                      {selectedWork.clientPhone && (
                        <div>
                          <span className="font-semibold text-gray-600">טלפון:</span>
                          <span className="mr-2 text-lg">{selectedWork.clientPhone}</span>
                        </div>
                      )}
                      <div>
                        <span className="font-semibold text-gray-600">תאריך:</span>
                        <span className="mr-2 text-lg">{selectedWork.date}</span>
                      </div>
                    </div>
                  </div>

                  {/* Work Details Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border-2 border-gray-300">
                      <thead>
                        <tr className="bg-orange-100">
                          <th className="border-2 border-gray-300 p-3 text-right font-bold">#</th>
                          <th className="border-2 border-gray-300 p-3 text-right font-bold">שם חלק</th>
                          <th className="border-2 border-gray-300 p-3 text-right font-bold">כמות</th>
                          <th className="border-2 border-gray-300 p-3 text-right font-bold">מחיר ליחידה</th>
                          <th className="border-2 border-gray-300 p-3 text-right font-bold">מחיר ללקוח</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workDetails.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="border-2 border-gray-300 p-6 text-center text-gray-500">
                              אין פריטים בעבודה זו
                            </td>
                          </tr>
                        ) : (
                          workDetails.map((item, index) => {
                            // מחיר ללקוח = מחיר ליחידה × כמות (סכום)
                            const calculatedCustomerPrice = (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0);
                            return (
                              <tr key={item.id} className="hover:bg-orange-50">
                                <td className="border-2 border-gray-300 p-3 text-gray-600">{index + 1}</td>
                                <td className="border-2 border-gray-300 p-3 font-medium">{item.partName}</td>
                                <td className="border-2 border-gray-300 p-3 text-gray-700">{item.quantity || '-'}</td>
                                <td className="border-2 border-gray-300 p-3 text-gray-700">
                                  {item.unitPrice ? `₪${item.unitPrice}` : '-'}
                                </td>
                                <td className="border-2 border-gray-300 p-3 text-orange-700 font-bold">
                                  ₪{calculatedCustomerPrice.toLocaleString()}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gradient-to-r from-orange-200 to-amber-200">
                          <td colSpan="4" className="border-2 border-gray-300 p-4 text-right">
                            <span className="text-xl font-bold text-gray-800">סה"כ מחיר ללקוח:</span>
                          </td>
                          <td className="border-2 border-gray-300 p-4">
                            <span className="text-2xl font-bold text-orange-700">
                              ₪{workDetails.reduce((sum, item) => {
                                // מחיר ללקוח = מחיר ליחידה × כמות (סכום)
                                const calculatedPrice = (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0);
                                return sum + calculatedPrice;
                              }, 0).toLocaleString()}
                            </span>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* No Selection */}
              {!selectedWork && !loadingWorkReport && (
                <div className="text-center py-12 text-gray-500">
                  <Briefcase className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg">בחר עבודה מהרשימה לצפייה בדוח</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info Section */}
        <div className="mt-8 bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">💡 טיפ לשימוש</h3>
          <ul className="text-blue-800 space-y-1">
            <li>• השתמש בחיפוש לפי שם כדי לסנן את הדיווחים</li>
            <li>• בחר טווח תאריכים כדי לראות נתונים מתקופה מסוימת</li>
            <li>• בחר דוח מהרשימה היורדת למעלה; לחץ על הכוכב כדי להוסיף דוח למועדפים</li>
            <li>• הדוח נפתח בתוך הדף, והתפריט נשאר למעלה כדי לעבור לדוח אחר מיד</li>
          </ul>
        </div>
        </>)}
      </div>
    </div>
  );
};

export default Reports;
